import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  RiCloudLine,
  RiLoader4Line,
  RiSearchLine,
  RiTimeLine,
  RiDatabase2Line,
  RiFileCodeLine,
  RiDeleteBin6Line,
  RiFileCopyLine,
} from '@remixicon/react';
import { useSchemaDesignerStore } from '@/stores/schema-designer-store';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

export const LoadCloudDialog = () => {
  const {
    savedSchemas,
    fetchCloudSchemas,
    loadFromCloud,
    deleteFromCloud,
    duplicateCloudSchema,
    isLoading,
  } = useSchemaDesignerStore();
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [loadingId, setLoadingId] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      fetchCloudSchemas();
    }
  }, [open, fetchCloudSchemas]);

  const cloudSchemas = savedSchemas.filter((s) => !s.id.startsWith('schema-'));
  const filteredSchemas = cloudSchemas.filter((schema) =>
    schema.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleLoad = async (schemaId: string) => {
    setLoadingId(schemaId);
    try {
      await loadFromCloud(schemaId);
      toast.success('Schema loaded successfully');
      setOpen(false);
    } catch (error: any) {
      toast.error(error.message || 'Failed to load schema');
    } finally {
      setLoadingId(null);
    }
  };

  const handleDelete = async (schemaId: string, schemaName: string) => {
    if (!confirm(`Are you sure you want to delete "${schemaName}"? This cannot be undone.`)) {
      return;
    }

    setDeletingId(schemaId);
    try {
      await deleteFromCloud(schemaId);
      toast.success('Schema deleted successfully');
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete schema');
    } finally {
      setDeletingId(null);
    }
  };

  const handleDuplicate = async (schemaId: string, schemaName: string) => {
    try {
      await duplicateCloudSchema(schemaId);
      toast.success(`Duplicated "${schemaName}"`);
    } catch (error: any) {
      toast.error(error.message || 'Failed to duplicate schema');
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2 bg-background shadow-sm">
          <RiFileCodeLine className="h-4 w-4" />
          Load from Cloud
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[700px] max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>Load Schema from Cloud</DialogTitle>
          <DialogDescription>
            Choose a schema from your cloud storage to load into the designer.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {/* Search */}
          <div className="relative">
            <RiSearchLine className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search schemas..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>

          {/* Schema List */}
          <ScrollArea className="h-[400px] pr-4">
            {isLoading && cloudSchemas.length === 0 ? (
              <div className="flex items-center justify-center h-32 text-muted-foreground">
                <RiLoader4Line className="h-6 w-6 animate-spin mr-2" />
                Loading schemas...
              </div>
            ) : filteredSchemas.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-32 text-center text-muted-foreground">
                <RiCloudLine className="h-8 w-8 mb-2 opacity-50" />
                {searchQuery ? 'No schemas match your search' : 'No cloud schemas found'}
                <p className="text-sm mt-1">
                  {!searchQuery && 'Save a schema to cloud to see it here'}
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredSchemas.map((schema) => (
                  <div
                    key={schema.id}
                    className="p-4 border border-border rounded-lg hover:border-primary/50 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 space-y-1">
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium">{schema.name}</h4>
                          {schema.isPublic && (
                            <Badge variant="secondary" className="text-xs">
                              Public
                            </Badge>
                          )}
                        </div>
                        {schema.description && (
                          <p className="text-sm text-muted-foreground line-clamp-2">
                            {schema.description}
                          </p>
                        )}
                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <RiDatabase2Line className="h-3 w-3" />
                            {schema.nodes.length} {schema.nodes.length === 1 ? 'table' : 'tables'}
                          </div>
                          <div className="flex items-center gap-1">
                            <RiTimeLine className="h-3 w-3" />
                            {formatDistanceToNow(new Date(schema.updatedAt), { addSuffix: true })}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDuplicate(schema.id, schema.name)}
                          disabled={isLoading}
                          className="h-8 w-8 p-0"
                        >
                          <RiFileCopyLine className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(schema.id, schema.name)}
                          disabled={deletingId === schema.id}
                          className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                        >
                          {deletingId === schema.id ? (
                            <RiLoader4Line className="h-4 w-4 animate-spin" />
                          ) : (
                            <RiDeleteBin6Line className="h-4 w-4" />
                          )}
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleLoad(schema.id)}
                          disabled={loadingId === schema.id}
                          className="ml-2"
                        >
                          {loadingId === schema.id ? (
                            <>
                              <RiLoader4Line className="h-4 w-4 mr-2 animate-spin" />
                              Loading...
                            </>
                          ) : (
                            'Load'
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </div>
      </DialogContent>
    </Dialog>
  );
};
